import {
  Stack,
  Text,
  Heading,
  Icon,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  Tooltip,
  TooltipContent,
  PanelDescription,
  Grid,
} from '@customerio/pluma-components/react';
import { formatThroughput, useWorkspaceHealthSimulation, useEnvHealth } from '@/features/workspace-health';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';

interface MetricRowProps {
  value: string;
  unit: string;
  label: string;
  description: string;
}

function MetricRow({ value, unit, label, description }: MetricRowProps) {
  return (
    <Stack gap="050">
      <InlineStack gap="050" alignBlock="baseline">
        <Heading level="3">{value}</Heading>
        <Text size="s" color="subtle">
          {unit}
        </Text>
      </InlineStack>
      <InlineStack gap="050" alignBlock="center">
        <Text size="xs" color="subtle">
          {label}
        </Text>
        <Tooltip>
          <Icon name="help" size="xxs" color="subtle" />
          <TooltipContent>{description}</TooltipContent>
        </Tooltip>
      </InlineStack>
    </Stack>
  );
}

export function StatsPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const stats = healthQuery.data?.stats;

  const ingressThroughput = stats?.throughput.ingress?.current ?? 0;
  const outgoingThroughput = stats?.throughput.outgoing?.current ?? 0;
  const throughputFormatted = formatThroughput(ingressThroughput);
  const outgoingFormatted = formatThroughput(outgoingThroughput);

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Live metrics</PanelTitle>
        <PanelDescription>Rates are current. Counts are since midnight UTC.</PanelDescription>
      </PanelHeader>
      <PanelSection>
        <Grid gap="200" columns={2}>
          <MetricRow
            value={throughputFormatted.replace(/\/s|\/min/, '')}
            unit={ingressThroughput >= 1 ? '/s' : '/min'}
            label="Data throughput"
            description="How fast data is being processed right now. This number may be low when there is nothing to process."
          />
          <MetricRow
            value={(stats?.processed_today.total?.current ?? 0).toLocaleString()}
            unit="records"
            label="Data processed today"
            description="Total records processed since midnight UTC, including events, profile updates, imports, and more."
          />
          <MetricRow
            value={outgoingFormatted.replace(/\/s|\/min/, '')}
            unit={outgoingThroughput >= 1 ? '/s' : '/min'}
            label="Outgoing throughput"
            description="How fast outgoing messages are being sent right now. This number may be low when there is nothing to send."
          />
          <MetricRow
            value={(stats?.processed_today.outgoing?.current ?? 0).toLocaleString()}
            unit="records"
            label="Sent today"
            description="Total outgoing messages sent since midnight UTC, including email, push, SMS, webhooks, and more."
          />
        </Grid>
      </PanelSection>
    </Panel>
  );
}
